import { RefreshStatus } from '../components/RefreshStatus';
import { Disclaimer } from '../components/Disclaimer';
import { TestIntro } from '../components/TestIntro';
import { paperTestSummary } from '../lib/paperTestPages';
import { Skeleton } from '../components/Skeleton';
import { useMakerForward } from '../hooks/useMakerForward';
import {
  ApiClientError,
  type MakerForwardResponse,
  type MakerRecentRound,
} from '../lib/apiClient';
import { MAKER_BACKTEST } from '../lib/btc15MakerBacktest';
import { formatSignedUsd } from '../lib/format';
import styles from './CrashBouncePage.module.css';

const cents = (x: number | null) =>
  x === null ? '—' : `${x > 0 ? '+' : x < 0 ? '−' : ''}${Math.abs(x).toFixed(2)}¢`;
const priceCents = (x: number) => `${Math.round(x * 100)}¢`;
const dayTime = (ms: number) =>
  new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const VERDICT_LABEL = {
  collecting: 'Collecting',
  passing: 'Passing',
  failing: 'Failing',
} as const;

export function MarketMakerPage() {
  const query = useMakerForward();
  const data = query.data;
  const error =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'Market Maker data is temporarily unavailable.';
  return (
    <div className={styles.page}>
      <TestIntro
        heading={
          <div className={styles.heading}>
            <h1>Market Maker</h1>
            <RefreshStatus
              updatedAt={query.dataUpdatedAt}
              isFetching={query.isFetching}
              isError={query.isError}
              onRefresh={() => query.refetch()}
            />
          </div>
        }
        summary={paperTestSummary('/market-maker')}
      >
        <p>
          Every other BTC 15m test here takes liquidity: it buys at the ask and pays
          Kalshi&apos;s taker fee, and every one of them has lost. This page tests the
          other seat. A market maker rests a bid and an ask inside the round and earns the
          spread when other traders hit them, paying no maker fee on this series. The
          catch is that quotes mostly get hit by traders who know more, so the question is
          whether the spread pays for that.
        </p>
      </TestIntro>
      <Disclaimer
        summary="Paper only — not a recommendation."
        text="Paper only. Nothing on this page places an order. This is a research experiment, not a recommendation to trade; a simulated quote can't know its place in Kalshi's queue, so real fills would be fewer and worse than shown."
      />
      {query.isLoading && (
        <>
          <p className="visually-hidden" role="status">
            Loading Market Maker…
          </p>
          <Skeleton width="100%" height="8rem" />
          <Skeleton width="100%" height="14rem" />
        </>
      )}
      {query.isError && !data && <p role="alert">{error}</p>}
      {data && (
        <>
          <Scorecard data={data} />
          <Markouts data={data} />
          <Backtest />
          <RecentRounds rounds={data.recent} />
          <DemoRunner />
          <p className={styles.fine}>
            Strategy {data.strategyId}: one contract on each side at the best bid and ask
            Kalshi showed that minute, minutes {data.strategy.firstMinute}–
            {data.strategy.lastMinute} of each round, only when the spread is at least{' '}
            {priceCents(data.strategy.minSpread)} and the price is between{' '}
            {priceCents(data.strategy.priceLow)} and {priceCents(data.strategy.priceHigh)}
            , never more than {data.strategy.maxInventory} contracts net, held to
            settlement. A quote fills when a public trade reaches it: <em>optimistic</em>{' '}
            counts a trade at our price (as if first in line), <em>pessimistic</em> only a
            trade through it. Quotes are Kalshi&apos;s minute-by-minute archive; trades
            are Kalshi&apos;s public trade tape. Locked{' '}
            {dayTime(Date.parse(data.forwardStart))}; only rounds after that count.{' '}
            {data.lastRunMs
              ? `Last scored ${dayTime(data.lastRunMs)}.`
              : 'Waiting for the first scoring run.'}
            {data.lastFailures.length > 0 &&
              ` Couldn't score ${data.lastFailures.join(', ')}; it retries on the next run.`}
          </p>
        </>
      )}
    </div>
  );
}

function Scorecard({ data }: { data: MakerForwardResponse }) {
  const s = data.summary;
  return (
    <section className={styles.card} aria-labelledby="maker-score-heading">
      <div className={styles.cardHead}>
        <h2 id="maker-score-heading">Forward test</h2>
        <span className={`${styles.verdict} ${styles[s.verdict]}`}>
          {VERDICT_LABEL[s.verdict]}
        </span>
      </div>
      <p className={styles.progress}>
        {s.rounds}/{data.passBar.minRounds} rounds · {s.pessimistic.fills} pessimistic
        fills · {s.pessimistic.roundsTwoSided} rounds filled both sides
      </p>
      <dl className={styles.stats}>
        <div>
          <dt>P&amp;L, pessimistic</dt>
          <dd className={tone(s.pessimistic.pnl)}>
            {s.rounds ? formatSignedUsd(s.pessimistic.pnl) : '—'}
          </dd>
        </div>
        <div>
          <dt>P&amp;L, optimistic</dt>
          <dd className={tone(s.optimistic.pnl)}>
            {s.rounds ? formatSignedUsd(s.optimistic.pnl) : '—'}
          </dd>
        </div>
        <div>
          <dt>Worst drawdown</dt>
          <dd>{s.rounds ? formatSignedUsd(s.pessimistic.maxDrawdown) : '—'}</dd>
        </div>
      </dl>
      {s.rounds > 0 && (
        <p className={styles.small}>
          Pessimistic P&amp;L splits into {formatSignedUsd(s.pessimistic.spreadCaptured)}{' '}
          of spread earned on matched buys and sells, and{' '}
          {formatSignedUsd(s.pessimistic.inventoryPnl)} from inventory carried to
          settlement.
          {s.halves &&
            ` First half ${formatSignedUsd(s.halves.first.pessimisticPnl)}, second half ${formatSignedUsd(s.halves.second.pessimisticPnl)}.`}
        </p>
      )}
      <p className={styles.small}>
        Passes after {data.passBar.minRounds} rounds if the pessimistic P&amp;L is above
        zero in both halves and the average settlement markout is positive. One contract a
        quote, so dollars here are per contract of size.
      </p>
    </section>
  );
}

function Markouts({ data }: { data: MakerForwardResponse }) {
  const m = data.summary.markouts;
  return (
    <section className={styles.card} aria-labelledby="maker-markout-heading">
      <h2 id="maker-markout-heading">Is the flow toxic?</h2>
      <p className={styles.small}>
        For every public trade since the lock, what the maker on the other side made per
        contract. Positive means takers are giving the spread away; negative means they
        know where the price is going and resting quotes lose.
      </p>
      <dl className={styles.stats}>
        <div>
          <dt>Held to settlement</dt>
          <dd className={tone(m.settlementCents)}>{cents(m.settlementCents)}</dd>
        </div>
        <div>
          <dt>One minute later</dt>
          <dd className={tone(m.oneMinuteCents)}>{cents(m.oneMinuteCents)}</dd>
        </div>
        <div>
          <dt>Trades</dt>
          <dd>{m.trades.toLocaleString()}</dd>
        </div>
      </dl>
    </section>
  );
}

function Backtest() {
  const b = MAKER_BACKTEST;
  if (!b) return null;
  return (
    <section className={styles.card} aria-labelledby="maker-backtest-heading">
      <h2 id="maker-backtest-heading">Backtest</h2>
      <p className={styles.small}>
        The same strategy over {b.rounds.toLocaleString()} earlier rounds ({b.window}),
        before the lock. {b.note}
      </p>
      <dl className={styles.stats}>
        <div>
          <dt>Settlement markout</dt>
          <dd className={tone(b.settlementCents)}>{cents(b.settlementCents)}</dd>
        </div>
        <div>
          <dt>P&amp;L, pessimistic</dt>
          <dd className={tone(b.pessimisticPnl)}>{formatSignedUsd(b.pessimisticPnl)}</dd>
        </div>
        <div>
          <dt>P&amp;L, optimistic</dt>
          <dd className={tone(b.optimisticPnl)}>{formatSignedUsd(b.optimisticPnl)}</dd>
        </div>
      </dl>
    </section>
  );
}

function RecentRounds({ rounds }: { rounds: MakerRecentRound[] }) {
  return (
    <section className={styles.card} aria-labelledby="maker-recent-heading">
      <h2 id="maker-recent-heading">Recent rounds</h2>
      {rounds.length === 0 ? (
        <p className={styles.small}>
          None scored yet. Each round is scored a few minutes after Kalshi settles it.
        </p>
      ) : (
        <ul className={styles.list}>
          {rounds.slice(0, 24).map((r) => (
            <li key={r.ticker} className={styles.row}>
              <div className={styles.rowHead}>
                <strong>{dayTime(r.closeMs)}</strong>
                <span className={tone(r.pessimistic.pnl)}>
                  {formatSignedUsd(r.pessimistic.pnl)}
                </span>
              </div>
              <p className={styles.small}>
                Settled {r.result === 'yes' ? 'YES (up)' : 'NO (down)'} ·{' '}
                {r.pessimistic.fills} fills
                {r.pessimistic.inventory !== 0 &&
                  `, held ${Math.abs(r.pessimistic.inventory)} ${r.pessimistic.inventory > 0 ? 'YES' : 'NO'}`}{' '}
                · optimistic {formatSignedUsd(r.optimistic.pnl)} ·{' '}
                {r.trades.toLocaleString()} trades, markout {cents(r.settlementCents)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DemoRunner() {
  return (
    <section className={styles.card} aria-labelledby="maker-demo-heading">
      <h2 id="maker-demo-heading">Next: quoting on the demo account</h2>
      <p className={styles.small}>
        A paper replay can&apos;t see its place in the queue, so the honest next step is
        posting these same quotes as real resting orders on Kalshi&apos;s play-money demo
        exchange and letting Kalshi decide the fills. That isn&apos;t built yet: the order
        path allows one immediate order per round, and resting quotes with cancels need
        changes to that safety code first. The plan is in{' '}
        <code>docs/MARKET_MAKER.md</code>, waiting on the owner&apos;s review.
      </p>
    </section>
  );
}

function tone(x: number | null) {
  if (x === null || x === 0) return styles.flat;
  return x > 0 ? styles.up : styles.down;
}
